import React, { useCallback, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { createMakeupAnalysisJob } from '../api/endpoints';
import { isCreditError } from '../api/config';
import { useUser } from '../contexts/UserContext';
import { navigateTo } from '../utils/navigation';
import { getCroppedImageBlob } from '../utils/cropImage';
import CameraCapture from '../components/CameraCapture';
import PhotoConsentGate from '../components/PhotoConsentGate';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';
import { useCreditGate } from '../hooks/useCreditGate';

const FEATURE_KEY = 'makeup_analysis';

const A4_ASPECT = 210 / 297;
const OUT_W = 768;
const OUT_H = Math.round(OUT_W / A4_ASPECT); // 1086

export type MakeupAnalysisScreenInput = Record<string, never>;

export interface MakeupAnalysisScreenResult {
  jobId: string;
}

const MakeupAnalysisScreen: React.FC<ScreenProps<MakeupAnalysisScreenInput, MakeupAnalysisScreenResult>> = ({
  mode,
  onComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [photoConsentGranted, setPhotoConsentGranted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { credits, costFor, refreshCredits } = useUser();
  // Credits gate the send, never the photo (SCRUM-196).
  const gate = useCreditGate(FEATURE_KEY);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18 };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 14, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: spring },
    exit: { opacity: 0, y: -8, transition: quick },
  };
  const innerStagger: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06, delayChildren: prefersReduced ? 0 : 0.05 } },
  };
  const innerItem: Variants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: spring } };

  const cost = costFor(FEATURE_KEY);
  const total = credits ? gate.spendable : null;
  const outOfQuota = gate.block === 'insufficient';

  const handleBlob = (blob: Blob) => {
    setImageSrc(URL.createObjectURL(blob));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    setStep(2);
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleBlob(file);
    e.currentTarget.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files.item(0);
    if (file === null) return;
    handleBlob(file);
  };

  const handleSubmit = async () => {
    if (!imageSrc || !croppedAreaPixels || uploading) return;
    if (!(await gate.ensure())) return;
    setUploading(true);
    setError(null);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, { outputWidth: OUT_W, outputHeight: OUT_H });
      const res = await createMakeupAnalysisJob(blob);
      void refreshCredits();
      if (mode === 'modal') {
        setUploading(false);
        onComplete?.({ jobId: res.job_id });
      } else {
        navigateTo(`/makeup-analysis/${res.job_id}/pending`);
      }
    } catch (error_: unknown) {
      if (isCreditError(error_)) {
        setError(error_.message);
        void refreshCredits();
      } else {
        setError(getErrorMessage(error_, 'Upload failed — please check your connection and try again.'));
      }
      setUploading(false);
    }
  };

  const PlusIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );

  const inner = (
    <>
      <PhotoConsentGate onAccepted={() => { setPhotoConsentGranted(true); }} />
      {gate.sheet}

      {photoConsentGranted && (
        <div className="page-guard w-full max-w-[100svw] overflow-x-hidden stack-5">
          <style>{`@supports not (width: 1svw) { .page-guard { max-width: 100vw; } }`}</style>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPicked}
            className="hidden"
            aria-hidden="true"
          />

          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
            <div className="text-center sm:text-left flex-1">
              {mode === 'page' && (
                <div className="row-3 justify-center sm:justify-start mb-2">
                  <h1 className="t-h2">Makeup Analysis</h1>
                </div>
              )}
              <p className="t-small t-mute max-w-sm">
                {step === 1
                  ? 'Upload an A4 portrait photo. No makeup preferred — shows your true undertone.'
                  : 'Crop to portrait frame — fit your face clearly, then analyse.'}
              </p>
            </div>

            {cost !== null && (
              <span
                className={outOfQuota ? 'pill' : 'pill pill--teal'}
                style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
              >
                {outOfQuota ? (
                  <><span className="dot dot--coral" />Need {cost} credits</>
                ) : (
                  <><span className="dot" />{cost} credits{total !== null ? ` · ${total} left` : ''}</>
                )}
              </span>
            )}
            {gate.note}
            {gate.unlockedLine}
          </div>

          <AnimatePresence mode="wait" initial={false}>
            {step === 1 && (
              <motion.section
                key="step1"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="relative group"
              >
                <div
                  className="absolute -inset-0.5 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700 group-hover:duration-200 pointer-events-none"
                  style={{ background: 'linear-gradient(135deg, rgba(196,103,58,0.12), rgba(196,103,58,0.04))' }}
                />
                <div className="card card--bordered relative flex flex-col items-center" style={{ borderRadius: 'var(--r-xl)', padding: 'var(--s-7) var(--s-6)' }}>
                  <motion.div variants={innerStagger} initial="hidden" animate="visible" className="w-full max-w-2xl stack-6">

                    <motion.div variants={innerItem} className="t-small t-mute text-center">
                      Photos are analyzed for cosmetic, appearance-based insights only. This service does not provide medical advice or diagnosis.
                    </motion.div>

                    <motion.div
                      variants={innerItem}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={onDrop}
                      className="drop-zone"
                    >
                      <>
                          <motion.div
                            className="relative mb-6"
                            animate={prefersReduced ? {} : { y: [0, -6, 0] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', repeatType: 'loop' }}
                          >
                            <motion.div
                              className="absolute -inset-4 pointer-events-none"
                              animate={prefersReduced ? {} : { scale: [1, 1.2, 1], opacity: [0.35, 0.08, 0.35] }}
                              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                              style={{ borderRadius: 'var(--r-lg)', background: 'rgba(196,103,58,0.12)', filter: 'blur(14px)' }}
                            />
                            <div className="drop-zone__icon drop-zone__icon--coral relative" style={{ width: 72, height: 72, borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-lifted)' }}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8" aria-hidden="true">
                                <path d="M3 12s3-4 9-4 9 4 9 4-3 4-9 4-9-4-9-4Z" />
                                <path d="M7 12s2 1 5 1 5-1 5-1" />
                              </svg>
                            </div>
                            <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 flex items-center justify-center bg-white" style={{ borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-soft)', color: 'var(--c-coral)' }}>
                              <PlusIcon className="w-4 h-4" />
                            </div>
                          </motion.div>
                          <h3 className="t-h3 mb-1">Upload Your Portrait</h3>
                          <p className="t-small t-mute mb-2">Drag image file or click to browse</p>
                          <span className="t-label" style={{ color: 'var(--c-coral)' }}>A4 Portrait · Undertone Analysis</span>
                          <div className="hidden sm:flex gap-2 mt-4">
                            <span className="pill">Portrait</span>
                            <span className="pill">Natural Light</span>
                          </div>
                        </>
                    </motion.div>

                    {(
                      <motion.div variants={innerItem} className="stack-4 items-center">
                        <div className="w-full flex items-center gap-4 t-label" style={{ color: 'var(--ink-muted)' }}>
                          <div className="flex-1 h-px" style={{ background: 'var(--rule)' }} />
                          or use live camera
                          <div className="flex-1 h-px" style={{ background: 'var(--rule)' }} />
                        </div>
                        <div
                          className="w-full max-w-sm overflow-hidden bg-black"
                          style={{ borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lifted)' }}
                        >
                          <CameraCapture onCapture={handleBlob} />
                        </div>
                      </motion.div>
                    )}

                    <AnimatePresence>
                      {error && (
                        <motion.p
                          key={error}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2 }}
                          className="t-small"
                          style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </motion.section>
            )}

            {step === 2 && imageSrc && (
              <motion.section
                key="step2"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="flex flex-col items-center"
              >
                <style>{`
                  .reactEasyCrop_CropArea { border: 1px solid rgba(255,255,255,0.3) !important; border-radius: 4px; box-shadow: 0 0 0 9999px rgba(0,0,0,0.85) !important; }
                  .reactEasyCrop_Container { background: #0a0a0a; }
                `}</style>

                <div className="w-full max-w-[480px]">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={spring}
                    className="relative w-full overflow-hidden bg-black"
                    style={{ height: 420, borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-deep)' }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={A4_ASPECT}
                      cropShape="rect"
                      showGrid={true}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      zoomWithScroll
                      minZoom={0.5}
                      maxZoom={6}
                      // Allow shrinking below 1x so a square (1:1) photo fits the
                      // A4 frame with padding instead of being hard-cropped;
                      // requires free positioning.
                      restrictPosition={false}
                    />
                    <div className="absolute bottom-3 left-3 pointer-events-none z-10">
                      <span className="hud-badge">A4 Portrait · {OUT_W}×{OUT_H}</span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={spring}
                    className="mt-6 stack-4 items-center"
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                      className="row-4 w-full px-5 py-3"
                      style={{ background: 'var(--c-cream)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-soft)', border: '1px solid var(--rule)' }}
                    >
                      <span className="t-label" style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>Zoom</span>
                      <div className="flex-1">
                        <input
                          type="range"
                          min={0.5}
                          max={6}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => { setZoom(Number.parseFloat(e.target.value)); }}
                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                          style={{ accentColor: 'var(--c-coral)', background: 'var(--rule)' }}
                        />
                      </div>
                      <span className="t-small" style={{ color: 'var(--ink)', fontWeight: 600, minWidth: 32 }}>{zoom.toFixed(1)}x</span>
                    </motion.div>

                    <AnimatePresence>
                      {error && (
                        <motion.p
                          key={error}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2 }}
                          className="t-small w-full"
                          style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <div style={{ display: 'flex', gap: 12 }}>
                      <motion.button
                        whileHover={prefersReduced ? {} : { y: -1 }}
                        whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                        onClick={() => { setStep(1); setImageSrc(null); setError(null); }}
                        className="btn btn--ghost btn--sm"
                        style={{ padding: '12px 24px' }}
                      >
                        Retake
                      </motion.button>
                      <motion.button
                        whileHover={prefersReduced ? {} : { y: -1 }}
                        whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                        onClick={handleSubmit}
                        disabled={!croppedAreaPixels || uploading}
                        className="btn btn--primary"
                        style={{ padding: '12px 32px', minWidth: 180 }}
                      >
                        {uploading ? (
                          <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(248,244,238,0.3)', borderTopColor: 'var(--c-cream)' }} />
                        ) : (
                          'Analyse Makeup'
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title="Makeup analysis"
        onClose={() => onCancel?.()}
        widthClass="max-w-3xl"
      >
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default MakeupAnalysisScreen;
